// Project Attachments Service for backend communication
import { api } from './api';

export interface ProjectAttachmentDto {
  id: string;
  projectId: string;
  attachmentType: string;
  fileName: string;
  fileUrl: string;
  fileSize?: number;
  mimeType?: string;
  uploadedAt: string;
}

export interface AddLinkRequest {
  url: string;
  title?: string;
}

// Project Attachments API
export const attachmentsApi = {
  // Get all attachments for a project
  getProjectAttachments: (projectId: string, attachmentType?: string): Promise<ProjectAttachmentDto[]> => {
    const params = attachmentType ? `?attachmentType=${attachmentType}` : '';
    return api.get<ProjectAttachmentDto[]>(`/projects/${projectId}/attachments${params}`);
  },

  // Get attachment by ID
  getById: (projectId: string, attachmentId: string): Promise<ProjectAttachmentDto> => {
    return api.get<ProjectAttachmentDto>(`/projects/${projectId}/attachments/${attachmentId}`);
  },

  // Add a link attachment
  addLink: (projectId: string, data: AddLinkRequest): Promise<ProjectAttachmentDto> => {
    return api.post<ProjectAttachmentDto>(`/projects/${projectId}/attachments/link`, data);
  },

  // Upload a file attachment (requires FormData)
  uploadFile: async (projectId: string, file: File, attachmentType: string): Promise<ProjectAttachmentDto> => {
    const formData = new FormData();
    formData.append('File', file);
    formData.append('AttachmentType', attachmentType);

    const token = localStorage.getItem('accessToken');
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    // Note: Don't set Content-Type header, browser will set it with boundary for multipart

    const response = await fetch(
      `${process.env.REACT_APP_API_URL || 'http://localhost:5202/api'}/projects/${projectId}/attachments`,
      {
        method: 'POST',
        headers,
        body: formData,
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to upload file');
    }

    return response.json();
  },

  // Remove an attachment
  remove: (projectId: string, attachmentId: string): Promise<void> => {
    return api.delete<void>(`/projects/${projectId}/attachments/${attachmentId}`);
  },
};

export default attachmentsApi;
